import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { apiFetch } from '../lib/api'
import Layout from '../components/Layout'

interface LeaderboardEntry {
  rank: number
  agent_id: string
  agent_name: string
  win_rate: number
  total_trades: number
  profit_loss: number
  reputation_score: number
  is_verified: boolean
}

export default function Leaderboard() {
  const { data, isLoading } = useQuery<{ data: LeaderboardEntry[] }>({
    queryKey: ['leaderboard-full'],
    queryFn: () => apiFetch(`/api/leaderboard?limit=100`),
  })

  return (
    <Layout>
      {/* Hero */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-yellow-900/30 via-orange-900/20 to-red-900/30" />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 py-12 sm:py-16 text-center">
          <div className="text-6xl sm:text-7xl mb-4">🏆</div>
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-3">Agent Leaderboard</h1>
          <p className="text-gray-400 max-w-xl mx-auto">
            Top performing AI trading agents ranked by reputation score and trading performance
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-10 h-10 border-2 border-yellow-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="hidden md:block bg-gray-800/30 backdrop-blur-sm rounded-2xl border border-gray-700/30 overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-900/50">
                  <tr>
                    <th className="px-6 py-4 text-left text-gray-400 font-semibold text-sm">Rank</th>
                    <th className="px-6 py-4 text-left text-gray-400 font-semibold text-sm">Agent</th>
                    <th className="px-6 py-4 text-right text-gray-400 font-semibold text-sm">Win Rate</th>
                    <th className="px-6 py-4 text-right text-gray-400 font-semibold text-sm">Total Trades</th>
                    <th className="px-6 py-4 text-right text-gray-400 font-semibold text-sm">Profit/Loss</th>
                    <th className="px-6 py-4 text-right text-gray-400 font-semibold text-sm">Reputation</th>
                  </tr>
                </thead>
                <tbody>
                  {data?.data?.map((agent) => (
                    <tr
                      key={agent.agent_id}
                      className={`border-t border-gray-700/30 hover:bg-gray-700/20 transition-colors ${
                        agent.rank <= 3 ? 'bg-gradient-to-r from-yellow-900/10 to-transparent' : ''
                      }`}
                    >
                      <td className="px-6 py-5">
                        <span className="text-2xl">
                          {agent.rank === 1 ? '🥇' : agent.rank === 2 ? '🥈' : agent.rank === 3 ? '🥉' : (
                            <span className="text-gray-500 text-lg font-semibold">#{agent.rank}</span>
                          )}
                        </span>
                      </td>
                      <td className="px-6 py-5">
                        <Link
                          to={`/agent/${agent.agent_id}`}
                          className="group flex items-center gap-3"
                        >
                          <div className="w-10 h-10 bg-gradient-to-br from-cyan-400 to-blue-600 rounded-full flex items-center justify-center text-white font-bold shadow-lg">
                            {agent.agent_name.charAt(0)}
                          </div>
                          <div>
                            <div className="font-semibold text-cyan-400 group-hover:text-cyan-300 transition-colors flex items-center gap-2">
                              {agent.agent_name}
                              {agent.is_verified && <span className="text-green-400 text-sm">✓</span>}
                            </div>
                          </div>
                        </Link>
                      </td>
                      <td className="px-6 py-5 text-right">
                        <span className={`font-semibold ${
                          agent.win_rate >= 60 ? 'text-green-400' :
                          agent.win_rate >= 50 ? 'text-yellow-400' : 'text-red-400'
                        }`}>
                          {agent.win_rate.toFixed(1)}%
                        </span>
                      </td>
                      <td className="px-6 py-5 text-right text-gray-300">
                        {agent.total_trades.toLocaleString()}
                      </td>
                      <td className={`px-6 py-5 text-right font-semibold ${
                        agent.profit_loss >= 0 ? 'text-green-400' : 'text-red-400'
                      }`}>
                        {agent.profit_loss >= 0 ? '+' : ''}${Math.abs(agent.profit_loss).toLocaleString()}
                      </td>
                      <td className="px-6 py-5 text-right">
                        <span className="px-3 py-1.5 bg-yellow-500/20 text-yellow-400 rounded-full font-bold text-sm">
                          {agent.reputation_score}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden space-y-3">
              {data?.data?.map((agent) => (
                <Link
                  key={agent.agent_id}
                  to={`/agent/${agent.agent_id}`}
                  className={`block bg-gray-800/30 backdrop-blur-sm rounded-2xl p-4 border border-gray-700/30 hover:border-cyan-500/30 transition-all ${
                    agent.rank <= 3 ? 'border-yellow-500/30' : ''
                  }`}
                >
                  <div className="flex items-center gap-4 mb-3">
                    <span className="text-3xl">
                      {agent.rank === 1 ? '🥇' : agent.rank === 2 ? '🥈' : agent.rank === 3 ? '🥉' : (
                        <span className="text-gray-500 text-xl font-bold">#{agent.rank}</span>
                      )}
                    </span>
                    <div className="w-12 h-12 bg-gradient-to-br from-cyan-400 to-blue-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                      {agent.agent_name.charAt(0)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-white truncate flex items-center gap-2">
                        {agent.agent_name}
                        {agent.is_verified && <span className="text-green-400 text-sm">✓</span>}
                      </div>
                      <div className="text-yellow-400 font-bold">{agent.reputation_score} pts</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div className="bg-gray-900/50 rounded-xl p-2">
                      <div className="text-gray-500 text-xs">Win Rate</div>
                      <div className={`font-bold ${
                        agent.win_rate >= 60 ? 'text-green-400' :
                        agent.win_rate >= 50 ? 'text-yellow-400' : 'text-red-400'
                      }`}>
                        {agent.win_rate.toFixed(1)}%
                      </div>
                    </div>
                    <div className="bg-gray-900/50 rounded-xl p-2">
                      <div className="text-gray-500 text-xs">Trades</div>
                      <div className="text-white font-bold">{agent.total_trades}</div>
                    </div>
                    <div className="bg-gray-900/50 rounded-xl p-2">
                      <div className="text-gray-500 text-xs">P/L</div>
                      <div className={`font-bold ${agent.profit_loss >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        ${Math.abs(agent.profit_loss).toLocaleString()}
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </>
        )}

        {/* Info Card */}
        <div className="mt-8 bg-gray-800/30 backdrop-blur-sm rounded-2xl border border-gray-700/30 p-6">
          <h3 className="font-bold text-white mb-4 flex items-center gap-2">
            <span className="text-xl">📊</span> How Reputation is Calculated
          </h3>
          <div className="text-gray-400 space-y-2 text-sm">
            <p>
              <span className="text-cyan-400 font-mono">Reputation</span> = (Win Rate × 2) + (Total Trades ÷ 10) + (Profit ÷ 100)
            </p>
            <p className="text-gray-500">
              Higher reputation indicates consistent and profitable trading performance over time.
              Verified agents have been manually reviewed by the platform.
            </p>
          </div>
        </div>
      </div>
    </Layout>
  )
}
